import { defineField, defineType } from 'sanity'
import getPageInfo, { seoGroup } from '../../objects/pageInfo'
import { getRegistrationCtaFields } from '../../pageBlocks/registrationCta'

const enabledField = () =>
  defineField({
    title: 'Enabled',
    name: 'enabled',
    type: 'boolean',
    initialValue: false,
  })

export default defineType({
  title: 'Home',
  name: 'home',
  type: 'document',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
  ],
  fields: [
    defineField({
      name: 'heroCarousel',
      title: 'Hero Carousel',
      type: 'array',
      of: [{ type: 'hero' }],
      validation: (rule) => rule.min(1),
      group: 'content',
    }),
    defineField({
      name: 'heroDuration',
      title: 'Hero Carousel Duration',
      type: 'number',
      description:
        'Duration of each slide in milliseconds (1000 ms  = 1 second)',
      initialValue: 5000,
      group: 'content',
    }),
    // APE-40: campaign blocks between the hero carousel and the vehicle selector,
    // each switched on independently.
    defineField({
      title: 'Campaign Countdown',
      name: 'campaignCountdown',
      type: 'object',
      description: 'Shown below the hero carousel while enabled.',
      group: 'content',
      fields: [
        enabledField(),
        defineField({
          title: 'Count down to',
          name: 'endDate',
          type: 'datetime',
          options: {
            dateFormat: 'YYYY-MM-DD',
            timeFormat: 'HH:mm',
          },
          description:
            'Shown in your browser time zone. The timer stops at zero; disable the block or change the date when the campaign moves on.',
          validation: (rule) =>
            rule.custom((value, context) => {
              const parent = context.parent as { enabled?: boolean } | undefined
              return parent?.enabled && !value
                ? 'Set the date before enabling the countdown'
                : true
            }),
        }),
        defineField({
          title: 'Title (h2)',
          name: 'title',
          type: 'string',
        }),
        defineField({
          title: 'Text',
          name: 'text',
          type: 'text',
          rows: 3,
        }),
        defineField({
          title: 'Theme',
          name: 'theme',
          type: 'string',
          options: {
            list: [
              { title: 'Black (black bg, white text)', value: 'black' },
              { title: 'Red (red bg, white text)', value: 'red' },
            ],
            layout: 'radio',
            direction: 'horizontal',
          },
          initialValue: 'black',
        }),
      ],
    }),
    defineField({
      title: 'Create Account CTA',
      name: 'registrationCta',
      type: 'object',
      description: 'Shown below the countdown (or the hero carousel) while enabled.',
      group: 'content',
      fields: [enabledField(), ...getRegistrationCtaFields()],
    }),
    defineField({
      title: 'Hero',
      name: 'hero',
      type: 'hero',
      group: 'content',
    }),
    defineField({
      title: '3 Feature Boxes',
      name: 'featureBoxes',
      type: 'threeBoxes',
      group: 'content',
    }),
    defineField({
      title: 'Promo Box',
      name: 'promo',
      type: 'hero',
      group: 'content',
    }),
    defineField({
      title: 'Difference list',
      name: 'differenceList',
      type: 'differenceList',
      group: 'content',
    }),
    defineField({
      title: 'Testimonials',
      name: 'testimonials',
      type: 'testimonials',
      group: 'content',
    }),
    defineField({
      title: '3 Feature Boxes',
      name: 'featureBoxes2',
      type: 'threeBoxes',
      group: 'content',
    }),
    defineField({
      title: 'Blog',
      name: 'blog',
      type: 'object',
      fields: [
        defineField({
          title: 'Section title',
          name: 'title',
          type: 'string',
        }),
        defineField({
          title: 'Articles',
          name: 'articles',
          type: 'array',
          of: [
            {
              type: 'reference',
              to: [{ type: 'article' }],
              options: {
                filter: `showOnHomePage`,
              },
            },
          ],
          validation: (rule) =>
            rule.unique().error('Each article should be unique'),
        }),
        defineField({
          title: 'Button list',
          name: 'buttonList',
          type: 'buttonList',
        }),
      ],
      group: 'content',
    }),
    getPageInfo({ lockCanonical: true }),
  ],
  preview: {
    select: {
      pageInfo: 'pageInfo',
    },
    prepare(selection) {
      const { title = 'New Page', description: subtitle = '' } =
        selection.pageInfo || {}
      return {
        title,
        subtitle,
      }
    },
  },
})